<script lang="ts">
	/**
	 * `/Settings/Admin/Customers/:uuid` — one customer, and everything support acts on (Design §9.6, §13).
	 *
	 * The uuid in the path is the **user's**: a customer is one row per user and has no external
	 * identifier of its own, so inventing one would give the same person two names in the URLs and
	 * in the audit trail.
	 *
	 * Three actions here move money or access, and all three carry a note into the audit trail:
	 * issuing a refund, cancelling a subscription immediately, and clearing a chargeback flag. The
	 * flag is deliberately not cleared by anything automatic — a lost dispute leaves it set, and
	 * only a person may decide the customer is trustworthy again.
	 */

	import { onMount } from 'svelte';
	import {
		Breadcrumb,
		Button,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell, Modal } from 'flowbite-svelte';
	import {
		CardPlaceholder,
		ConfirmButton,
		EmptyState,
		FormField,
		InvoiceDocumentLinks,
		ModalHeader,
		PageHeader,
		Panel,
		PromptButton,
		RefundCreditNoteCell,
		RoutedBreadcrumbItem,
		RoutedLink,
		SkeletonWrapper,
		SubscriptionStatusBadge, FormModal } from '../../components';
	import RefundForm from './customers/RefundForm.svelte';
	import AcceptedTermsPanel from './customers/AcceptedTermsPanel.svelte';
	import VisitsPanel from './customers/VisitsPanel.svelte';
	import {
		cancelSubscriptionNow,
		getAdminCustomer,
		grantEntitlement,
		retryAdminRefund,
		revokeEntitlement,
		unflagCustomer,
		updateUser
	} from '../../AppNetwork';
	import { formatAmount } from '../../stores/storefront';
	import { formatCalendarDate, invoiceReference, invoiceStatusLabel } from '../../Utilities';
	import { toastError, toastSuccess } from '../../stores/toast';
	import type { AdminCustomerDetail, AdminInvoice, StoreRefund } from '../../AppTypes';

	export let params: { uuid?: string } = {};

	let detail: AdminCustomerDetail | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	/** The invoice whose refund form is open, if any. One at a time, deliberately. */
	let refunding: AdminInvoice | null = null;
	let busy: string = '';

	/** The comp form. Held here rather than in a component so the page keeps one load path. */
	let isGranting: boolean = false;
	let grantKey: string = '';
	let grantNote: string = '';
	let grantExpiresAt: string = '';

	$: uuid = params.uuid ?? '';

	/** Named in words rather than shown as a raw enum: `one_time` is not a label. */
	const sourceCopy: Record<'subscription' | 'one_time' | 'manual', string> = {
		subscription: 'Subscription',
		one_time: 'Purchase',
		manual: 'Comp'
	};

	const refundStatusCopy: Record<StoreRefund['status'], string> = {
		pending: 'Pending',
		succeeded: 'Succeeded',
		failed: 'Failed',
		canceled: 'Reversed'
	};

	const refundReasonCopy: Record<StoreRefund['reason'], string> = {
		customer_self_serve: 'Customer',
		admin: 'Admin',
		chargeback: 'Chargeback'
	};

	async function fnLoad(userUUID: string): Promise<void> {
		if (userUUID === '') return;

		loading = true;
		loadError = '';
		try {
			detail = await getAdminCustomer(userUUID);
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	/**
	 * Promote or demote, on the page that says who this person is.
	 *
	 * It lives here rather than on the list for the reason the two screens were merged in the
	 * first place: the role and the purchase history are two facts about one person, and a
	 * promotion made from a list of names is made with none of the context that would justify it
	 * — no sign of whether this is the customer who opened four chargebacks or the colleague who
	 * has never bought anything.
	 *
	 * Confirmed rather than a bare toggle, and the confirmation names the person: administrator
	 * grants every admin route in the store, and the two rows either side of the one somebody
	 * meant to click look exactly like it.
	 */
	async function fnSetAdmin(isAdmin: boolean): Promise<void> {
		if (detail === null) return;
		busy = 'role';
		try {
			await updateUser(detail.customer.userID, { isAdmin });
			toastSuccess(isAdmin ? 'This person can now administer the store.' : 'Administrator access removed.');
			await fnLoad(uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	async function fnUnflag(note: string): Promise<void> {
		busy = 'unflag';
		try {
			await unflagCustomer(uuid, note);
			toastSuccess('Flag cleared. The customer can start new checkouts again.');
			await fnLoad(uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	async function fnCancelNow(subscriptionUUID: string, note: string): Promise<void> {
		busy = subscriptionUUID;
		try {
			await cancelSubscriptionNow(subscriptionUUID, note);
			toastSuccess('Subscription ended. Refunding is a separate decision.');
			await fnLoad(uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/** Re-submits the same refund row under the same key — never a second refund. */
	async function fnRetry(refundUUID: string): Promise<void> {
		busy = refundUUID;
		try {
			await retryAdminRefund(refundUUID);
			toastSuccess('Refund re-submitted to the payment provider.');
			await fnLoad(uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/**
	 * A comp (Design §4.4).
	 *
	 * `manual` is a first-class entitlement source that §7.1 reads from `entitlement_grants`, and
	 * the note is **mandatory** on the server — an entitlement granted for no stated reason is
	 * indistinguishable from a mistake six months later. Without this control, comping an account
	 * means opening a SQL client.
	 */
	async function fnGrant(): Promise<void> {
		if (grantKey.trim() === '' || grantNote.trim() === '') {
			toastError('An entitlement key and a note are both required.');
			return;
		}

		busy = 'grant';
		try {
			await grantEntitlement(uuid, {
				entitlementKey: grantKey.trim(),
				expiresAt: grantExpiresAt === '' ? null : new Date(grantExpiresAt).toISOString(),
				note: grantNote.trim()
			});
			toastSuccess(`Granted ${grantKey.trim()}.`);
			// Closed only on success: a refusal keeps the form open with what was typed still in it.
			isGranting = false;
			grantKey = '';
			grantNote = '';
			grantExpiresAt = '';
			await fnLoad(uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	/**
	 * Soft-revokes the grant. A `POST`, never a `DELETE`: entitlement rows are never deleted,
	 * because a deleted row restarts `version` and permanently locks out every consumer that has
	 * already cached a higher one.
	 *
	 * It revokes the **grant**, not the entitlement: a key the customer also holds through a paid
	 * subscription stays, which is why the confirm copy says so rather than promising removal.
	 */
	async function fnRevoke(entitlementKey: string): Promise<void> {
		busy = `revoke:${entitlementKey}`;
		try {
			await revokeEntitlement(uuid, entitlementKey);
			toastSuccess(`Revoked the manual grant of ${entitlementKey}.`);
			await fnLoad(uuid);
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			busy = '';
		}
	}

	async function fnRefunded(): Promise<void> {
		refunding = null;
		await fnLoad(uuid);
	}

	onMount(() => void fnLoad(uuid));
</script>

<Breadcrumb class="mb-4">
	<RoutedBreadcrumbItem href="/" home>Store</RoutedBreadcrumbItem>
	<RoutedBreadcrumbItem href="/Settings/Admin/Customers">Customers</RoutedBreadcrumbItem>
	<!-- Last crumb carries no `href`, so it renders muted. -->
	<RoutedBreadcrumbItem>
		{detail?.customer.billingName ?? detail?.customer.displayName ?? 'Customer'}
	</RoutedBreadcrumbItem>
</Breadcrumb>

<PageHeader title={detail?.customer.billingName ?? detail?.customer.displayName ?? 'Customer'}>
	<svelte:fragment slot="description">
		{detail?.customer.billingEmail ?? detail?.customer.email ?? ''}
	</svelte:fragment>
</PageHeader>

{#if loading}
	<SkeletonWrapper>
		<CardPlaceholder />
	</SkeletonWrapper>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="This customer could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => fnLoad(uuid)}>Try again</Button>
	</EmptyState>
{:else if detail !== null}
	<Panel title="Billing" faIcon="id-card">
		<dl class="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
			<div>
				<dt class="card-label">Currency</dt>
				<dd>{detail.customer.currency?.toUpperCase() ?? 'Not pinned yet'}</dd>
			</div>
			<div>
				<dt class="card-label">Provider credit</dt>
				<!-- Negative is credit. It is why a "full refund" can come up short: that part
				     never moved through the charge, so it cannot come back through it. -->
				<dd>
					{detail.customer.balance === 0
						? 'None'
						: ($formatAmount(detail.customer.balance, detail.customer.currency) ??
							String(detail.customer.balance))}
				</dd>
			</div>
			<div>
				<!--
					"Joined", not "Customer since": this page opens for people who have never bought
					anything now, and the date is when the account appeared either way.
				-->
				<dt class="card-label">Joined</dt>
				<dd>{formatCalendarDate(detail.customer.createdAt)}</dd>
			</div>
			<div>
				<dt class="card-label">Role</dt>
				<dd>
					{#if detail.customer.isAdmin}
						<span class="text-green-700 dark:text-green-400">Administrator</span>
					{:else}
						Customer
					{/if}
				</dd>
			</div>
			<div>
				<dt class="card-label">Status</dt>
				<dd>
					{#if detail.customer.isFlagged}
						<span class="text-red-600 dark:text-red-400">Flagged</span>
					{:else}
						Normal
					{/if}
				</dd>
			</div>
		</dl>

		<!--
			Where the role is changed. A `ConfirmButton` rather than a toggle: administrator grants
			every admin route in the store, and a switch that acts on its first click is one
			mis-aimed press away from handing that to the wrong person — the rows either side of the
			one somebody meant look exactly like it.
		-->
		<div class="mt-4 flex flex-wrap items-center gap-3">
			<p class="text-sm text-default">
				{#if detail.customer.isAdmin}
					This person can administer the store: every screen under Settings, including this one.
				{:else}
					Administrators reach every screen under Settings, including refunds and this page.
				{/if}
			</p>
			<ConfirmButton
				color="alternative"
				size="xs"
				disabled={busy === 'role'}
				action={detail.customer.isAdmin
					? `remove administrator access from ${detail.customer.displayName ?? detail.customer.email ?? 'this person'}`
					: `make ${detail.customer.displayName ?? detail.customer.email ?? 'this person'} an administrator`}
				on:confirm={() => fnSetAdmin(!(detail?.customer.isAdmin ?? false))}
			>
				<i class="fa-solid fa-user-shield mr-1.5"></i>
				{detail.customer.isAdmin ? 'Remove admin' : 'Make admin'}
			</ConfirmButton>
		</div>

		{#if detail.customer.isFlagged}
			<div class="mt-4 flex flex-wrap items-center gap-3">
				<p class="text-sm text-default">
					{detail.customer.flaggedReason ?? 'Flagged.'} New checkouts are blocked until this is
					cleared.
				</p>
				<PromptButton
					color="alternative"
					size="xs"
					icon="flag"
					disabled={busy === 'unflag'}
					confirmText="Clear the chargeback flag?"
					confirmDetail="New purchases are allowed again. Say why, because the note is recorded in the audit trail."
					confirmButtonText="Clear flag"
					inputPlaceholder="Reason"
					on:confirm={event => fnUnflag(String(event.detail ?? ''))}
				>
					Clear flag
				</PromptButton>
			</div>
		{/if}
	</Panel>

	<!--
		§12.10 — this customer's visits, summarised here and explored elsewhere.

		The last few are on the page because that is how a support agent reads a customer record: top
		to bottom, once. A link on its own makes the answer something you have to already know to go
		looking for, and "they say they tried twice and it did not work" is a question that arrives
		without warning.
	-->
	<VisitsPanel {uuid} />

	<Panel title="Entitlements" faIcon="key" class="mt-6">
		{#if detail.entitlements.length === 0}
			<EmptyState title="Nothing held." description="No entitlement is active on this account." />
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Key</TableHeadCell>
					<TableHeadCell>Product</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Expires</TableHeadCell>
					<TableHeadCell>Source</TableHeadCell>
					<TableHeadCell><span class="sr-only">Actions</span></TableHeadCell>
				</TableHead>
				<TableBody>
					{#each detail.entitlements as entitlement (entitlement.entitlementKey)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">{entitlement.entitlementKey}</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!--
									The product it grants, linked. A manual grant and an entitlement for
									a product since deleted both carry no slug, and a link with nothing
									behind it is worse than plain text — so the name renders bare there.
								-->
								{#if entitlement.productSlug !== null && entitlement.productName !== null}
									<RoutedLink
										to={`/Product/${entitlement.productSlug}`}
										class="text-primary-500 hover:underline"
									>
										{entitlement.productName}
									</RoutedLink>
								{:else}
									{entitlement.productName ?? '—'}
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{entitlement.status}</TableBodyCell>
							<TableBodyCell class="dataTable">
								{entitlement.expiresAt === null ? 'Never' : formatCalendarDate(entitlement.expiresAt)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{sourceCopy[entitlement.source]}</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if entitlement.source === 'manual'}
									<ConfirmButton
										size="xs"
										color="alternative"
										disabled={busy === `revoke:${entitlement.entitlementKey}`}
										action="revoke this comp"
										confirmText="Revoke this comp? Access granted by a paid subscription or purchase is unaffected."
										on:confirm={() => fnRevoke(entitlement.entitlementKey)}
									>
										Revoke comp
									</ConfirmButton>
								{:else}
									<span class="text-xs text-default">Paid, refund to revoke</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}

		<!--
			The comp form (§4.4). Kept inside the panel it acts on so the state it changes and the
			control that changes it are read together.
		-->
		<div class="mt-6 border-t border-widget-color pt-4">
			<p class="card-label">
				<i class="fa-solid fa-gift text-[11px] mr-2 text-default" aria-hidden="true" />Grant a comp
			</p>
			<p class="text-sm text-default mb-3">
				A manual grant is a first-class entitlement source and survives every recompute. The note
				is required and goes into the audit trail, because an entitlement given for no stated reason is
				indistinguishable from a mistake six months later.
			</p>
			<Button size="sm" color="alternative" on:click={() => (isGranting = true)}>
				<i class="fa-solid fa-plus mr-2" />Grant an entitlement
			</Button>

			<FormModal
				bind:open={isGranting}
				title="Grant a comp"
				faIcon="gift"
				submitLabel="Grant entitlement"
				busy={busy === 'grant'}
				canSubmit={grantKey.trim().length > 0 && grantNote.trim().length > 0}
				description="A manual grant survives every recompute, and the note goes into the audit trail."
				on:submit={fnGrant}
			>
				<FormField id="grant-key" label="Entitlement key" required description="The key an application checks, e.g. polyverse.">
					<Input id="grant-key" bind:value={grantKey} placeholder="polyverse" />
				</FormField>
				<FormField id="grant-expires" label="Expires" description="Leave empty for a grant that never expires.">
					<Input id="grant-expires" type="date" bind:value={grantExpiresAt} />
				</FormField>
				<FormField id="grant-note" label="Note" required description="Why this account is being comped. Required.">
					<Input id="grant-note" bind:value={grantNote} placeholder="Goodwill after the March outage" />
				</FormField>
			</FormModal>
		</div>
	</Panel>

	<Panel title="Subscriptions" faIcon="repeat" class="mt-6">
		{#if detail.subscriptions.length === 0}
			<EmptyState title="No subscriptions." description="Nothing recurring on this account." />
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Product</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Renews</TableHeadCell>
					<TableHeadCell>Action</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each detail.subscriptions as subscription (subscription.uuid)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<!--
									The subscription's own screen, which is where everything this page
									cannot show lives — its invoices, the terms it was bought under, the
									options taken on it and the change history. Support arriving from a
									customer had no way through to it at all.
								-->
								<RoutedLink
									to={`/Settings/Admin/Transactions/Subscription/${subscription.uuid}`}
									class="text-primary-500 hover:underline"
								>
									{subscription.productName}
								</RoutedLink>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<SubscriptionStatusBadge status={subscription.status} />
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{subscription.currentPeriodEnd === null
									? '—'
									: formatCalendarDate(subscription.currentPeriodEnd)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if subscription.status !== 'canceled' && subscription.status !== 'expired'}
									<!-- Immediate, not at period end: it takes access the customer has paid
									     for, so it needs a stated reason and a person behind it (§9.4). -->
									<PromptButton
										color="alternative"
										size="xs"
										icon="ban"
										disabled={busy === subscription.uuid}
										confirmText="End this subscription now?"
										confirmDetail="Access stops immediately, and any refund is a separate decision."
										confirmButtonText="End now"
										inputPlaceholder="Reason"
										on:confirm={event => fnCancelNow(subscription.uuid, String(event.detail ?? ''))}
									>
										End now
									</PromptButton>
								{:else}
									<span class="text-default text-xs">Ended</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Panel>

	<Panel title="Invoices" faIcon="file-invoice-dollar" class="mt-6">
		{#if detail.invoices.length === 0}
			<EmptyState title="No invoices." description="Nothing has been billed on this account." />
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Invoice</TableHeadCell>
					<TableHeadCell>Paid</TableHeadCell>
					<TableHeadCell>Total</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Document</TableHeadCell>
					<TableHeadCell>Refund</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each detail.invoices as invoice (invoice.uuid)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<div>{invoiceReference(invoice)}</div>
								<!--
									The purchase it was raised for. An invoice is never the whole story —
									the refund history, the terms and the change log live on the sale —
									and this list was the one place naming both with no way between them.
									A subscription's invoice and a one-time purchase's invoice go to
									different screens, and one carrying neither is a manual invoice with
									no sale behind it, which is plain text rather than a dead link.
								-->
								{#if invoice.subscriptionUUID !== null}
									<RoutedLink
										to={`/Settings/Admin/Transactions/Subscription/${invoice.subscriptionUUID}`}
										class="text-xs text-primary-500 hover:underline"
									>
										{invoice.productName ?? 'Subscription'}
									</RoutedLink>
								{:else if invoice.orderUUID !== null}
									<RoutedLink
										to={`/Settings/Admin/Transactions/Order/${invoice.orderUUID}`}
										class="text-xs text-primary-500 hover:underline"
									>
										{invoice.productName ?? 'Purchase'}
									</RoutedLink>
								{:else}
									<div class="text-xs text-default">{invoice.productName ?? ''}</div>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{invoice.paidAt === null ? '—' : formatCalendarDate(invoice.paidAt)}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{$formatAmount(invoice.total, invoice.currency) ?? '—'}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{invoiceStatusLabel(invoice)}</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!-- Ours or theirs, with the credit notes beside it. `admin` chooses the
								     endpoint and is a prop rather than a guess from the route. -->
								<InvoiceDocumentLinks {invoice} admin />
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if invoice.status === 'paid'}
									<Button
										size="xs"
										color="alternative"
										on:click={() => (refunding = refunding?.uuid === invoice.uuid ? null : invoice)}
									>
										{refunding?.uuid === invoice.uuid ? 'Close' : 'Refund…'}
									</Button>
								{:else}
									<span class="text-default text-xs">Nothing to refund</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}

		<!--
			Refunding sits over the invoice list rather than under it: the admin is acting on a
			specific row, and losing sight of which one is exactly the mistake that matters here.
		-->
		<Modal
			open={refunding !== null}
			size="md"
			title=""
			on:close={() => (refunding = null)}
		>
			<!-- A slotted fragment has to be a direct child of the component, so the guard goes
			     inside it rather than around it. -->
			<svelte:fragment slot="header">
				{#if refunding !== null}
					<ModalHeader
						title="Refund invoice {invoiceReference(refunding)}"
						faIcon="money-bill-transfer"
						tone="danger"
					/>
				{/if}
			</svelte:fragment>

			{#if refunding !== null}
				<div data-testid="refund-panel">
					<RefundForm
						invoice={refunding}
						on:issued={() => void fnRefunded()}
						on:cancel={() => (refunding = null)}
					/>
				</div>
			{/if}
		</Modal>
	</Panel>

	<Panel title="Refunds" faIcon="money-bill-transfer" class="mt-6">
		{#if detail.refunds.length === 0}
			<EmptyState title="No refunds." description="Nothing has gone back out on this account." />
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Issued</TableHeadCell>
					<TableHeadCell>Amount</TableHeadCell>
					<TableHeadCell>Reason</TableHeadCell>
					<TableHeadCell>Entitlement</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Credit note</TableHeadCell>
					<TableHeadCell>Note</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each detail.refunds as refund (refund.uuid)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">{formatCalendarDate(refund.createdAt)}</TableBodyCell>
							<TableBodyCell class="dataTable">
								{$formatAmount(refund.amount, refund.currency) ?? refund.amount}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{refundReasonCopy[refund.reason]}</TableBodyCell>
							<TableBodyCell class="dataTable">
								{refund.revokedEntitlement ? 'Revoked' : 'Kept'}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{refundStatusCopy[refund.status]}
								{#if refund.providerRefundID === null && refund.status === 'pending'}
									<!-- Never sent, or sent and never answered. Retrying re-uses this row's
									     own idempotency key, so it cannot become a second refund. -->
									<div class="mt-1">
										<Button
											size="xs"
											color="alternative"
											disabled={busy === refund.uuid}
											on:click={() => void fnRetry(refund.uuid)}
										>
											Retry
										</Button>
									</div>
								{/if}
							</TableBodyCell>
							<!-- §4.6. Three answers rather than two — see the component. -->
							<TableBodyCell class="dataTable">
								<RefundCreditNoteCell {refund} on:issued={() => void fnLoad(uuid)} />
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<span class="text-xs text-default">{refund.note ?? ''}</span>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Panel>

	<!--
		§4.7 — everything this customer has ever agreed to, grouped by the purchase it was agreed
		for. Last on the page because it is the panel read least often: these are legal texts, and
		the questions this screen is opened with are about money. It is grouped rather than flat
		because an acceptance only means anything against the thing that was bought — the same term
		accepted twice is two different pieces of evidence.
	-->
	<AcceptedTermsPanel acceptances={detail.acceptedTerms} />
{/if}
